// A carved square cell cut corner to corner, twin of MapLibrary::CUTS and
// MapLibrary.normalize_terrain (docs/architecture/maps.md, "Library and
// terrain"): a scene's terrain `cuts` holds one string per row of cells, aligned
// with `rows`, "." for no cut and the letter of the corner that is ROCK for a
// cut cell, whose other half is floor of the code `rows` holds. `corners` holds
// the floor of the rock corner's half when it is floor too.
import { EMPTY } from "./terrain";

export type CutCorner = "ne" | "se" | "sw" | "nw";

// The corner each letter names, the one place both directions are read from.
export const CUT_CORNERS = { a: "ne", b: "se", c: "sw", d: "nw" } as const satisfies Record<string, CutCorner>;
export type CutLetter = keyof typeof CUT_CORNERS;
export const CUT_LETTERS = Object.keys(CUT_CORNERS) as CutLetter[];
export const CORNER_LETTERS = Object.fromEntries(CUT_LETTERS.map((letter) => [CUT_CORNERS[letter], letter])) as Record<CutCorner, CutLetter>;

type CutLines = readonly string[] | null | undefined;

const isLines = (value: unknown): value is string[] => Array.isArray(value) && value.every((line) => typeof line === "string");

function trim(lines: string[]) {
	const trimmed = lines.map((line) => line.replace(/\.+$/, ""));
	while (trimmed.length && trimmed.at(-1) === "") trimmed.pop();
	return trimmed;
}

// Stable, so an identity check holds while a terrain has no cuts.
export const NO_CUTS: readonly string[] = [];

export type Side = "n" | "e" | "s" | "w";
type Point = readonly [number, number];

// The sides of a cut cell that run along its rock corner: no floor lies along them.
export const ROCK_SIDES: Record<CutLetter, readonly Side[]> = { a: ["n", "e"], b: ["e", "s"], c: ["s", "w"], d: ["n", "w"] };

// A cut cell's floor half in unit-cell coordinates, clockwise on screen (y down)
// and starting with the diagonal, so the ground lies on the right of every edge.
export const FLOOR_HALF: Record<CutLetter, readonly [Point, Point, Point]> = {
	a: [
		[0, 0],
		[1, 1],
		[0, 1],
	],
	b: [
		[1, 0],
		[0, 1],
		[0, 0],
	],
	c: [
		[1, 1],
		[0, 0],
		[1, 0],
	],
	d: [
		[0, 1],
		[1, 0],
		[1, 1],
	],
};

// Each letter's rock sides as a bit mask, indexed by the letter's offset from "a", so floorAlong
// reads two character codes and no string or list: it runs for every side of every cell of an outline.
const SIDE_BIT: Record<Side, number> = { n: 1, e: 2, s: 4, w: 8 };
const ROCK_MASKS = CUT_LETTERS.map((letter) => ROCK_SIDES[letter].reduce((mask, side) => mask | SIDE_BIT[side], 0));
const FIRST_LETTER = CUT_LETTERS[0].charCodeAt(0);
const EMPTY_CODE = EMPTY.charCodeAt(0);

// Whether the cell has floor along one of its four sides: a carved cell does on
// every side but the two around a cut's rock corner, unless that corner is floor
// too (`corners`, the floor of the half the cut names). The one answer outlines,
// walls and the grid read, so a cut cell is never told apart by its callers.
export function floorAlong(rows: readonly string[], cuts: CutLines, col: number, row: number, side: Side, corners?: CutLines): boolean {
	const code = rows[row]?.charCodeAt(col);
	// A missing row, or a column outside the row, is no code (NaN), and uncarved.
	if (code === undefined || Number.isNaN(code) || code === EMPTY_CODE) return false;
	const offset = (cuts?.[row]?.charCodeAt(col) ?? Number.NaN) - FIRST_LETTER;
	if (!(offset >= 0 && offset < ROCK_MASKS.length) || (ROCK_MASKS[offset] & SIDE_BIT[side]) === 0) return true;
	const corner = corners?.[row]?.charCodeAt(col) ?? Number.NaN;
	return !Number.isNaN(corner) && corner !== EMPTY_CODE;
}

// The code a cell presents along one side: its own, or its corner's along the two sides of a cut's rock corner; null for none.
export function codeAlong(rows: readonly string[], cuts: CutLines, corners: CutLines, col: number, row: number, side: Side): string | null {
	const code = rows[row]?.[col];
	if (!code || code === EMPTY) return null;
	const letter = cutAt(cuts, col, row);
	return letter && ROCK_SIDES[letter].includes(side) ? cornerCode(cuts, corners, col, row) : code;
}

// The floor code of a cut cell's split-off half, or null when that half is rock (or the cell is not cut).
export function cornerCode(cuts: CutLines, corners: CutLines, col: number, row: number): string | null {
	const code = corners?.[row]?.[col];
	return code && code !== EMPTY && cutAt(cuts, col, row) ? code : null;
}

// What the outline, the rock band and the cave plan read: a cut cell whose split-off half is floor has no rock, so it
// is a whole cell to them. The same array comes back while no corner is floor, and the last result is kept.
let rocked: { cuts: CutLines; corners: CutLines; result: readonly string[] } | null = null;
export function rockCuts(cuts: readonly string[] = NO_CUTS, corners?: readonly string[]): readonly string[] {
	if (!corners?.length) return cuts;
	if (rocked?.cuts === cuts && rocked.corners === corners) return rocked.result;
	const result = trim(cuts.map((line, row) => [...line].map((letter, col) => ((corners[row]?.[col] ?? EMPTY) === EMPTY ? letter : EMPTY)).join("")));
	rocked = { cuts, corners, result };
	return result;
}

// Every cut cell with floor in both halves, in reading order, with the split-off half's code.
export function cornerFloorsOf(rows: readonly string[], cuts: CutLines, corners: CutLines): { col: number; row: number; letter: CutLetter; code: string }[] {
	return cutCellsOf(rows, cuts).flatMap(({ col, row, letter }) => {
		const code = cornerCode(cuts, corners, col, row);
		return code ? [{ col, row, letter, code }] : [];
	});
}

// The triangle of a cut cell the cut names, in unit-cell coordinates: the floor half of the opposite cut.
export function cornerHalf(letter: CutLetter): readonly [Point, Point, Point] {
	return FLOOR_HALF[opposite(letter)];
}

// Which half of a cut cell a point falls in, from its fractions of the cell (0..1 from the north-west corner):
// the corner half is the one holding the cut's corner.
export function halfAt(letter: CutLetter, x: number, y: number): "main" | "corner" {
	const toward = { a: x - y, b: x + y - 1, c: y - x, d: 1 - x - y }[letter];
	return toward > 0 ? "corner" : "main";
}

// Every cut on a carved cell, in reading order.
export function cutCellsOf(rows: readonly string[], cuts: CutLines): { col: number; row: number; letter: CutLetter; code: string }[] {
	const found: { col: number; row: number; letter: CutLetter; code: string }[] = [];
	cuts?.forEach((line, row) => {
		[...line].forEach((_, col) => {
			const letter = cutAt(cuts, col, row);
			const code = rows[row]?.[col];
			if (letter && code && code !== EMPTY) found.push({ col, row, letter, code });
		});
	});
	return found;
}

export function cutAt(cuts: CutLines, col: number, row: number): CutLetter | null {
	const letter = cuts?.[row]?.[col];
	return letter && letter in CUT_CORNERS ? (letter as CutLetter) : null;
}

// A cell's border letter: "." straight, "i" irregular, "r" rounded; a cell with no letter is straight.
export const STRAIGHT = ".";
export const borderAt = (borders: CutLines, col: number, row: number) => borders?.[row]?.[col] ?? STRAIGHT;

// Irregular beats rounded beats straight, the border two cells meeting on a curve take.
const ROUGHNESS: Record<string, number> = { ".": 0, r: 1, i: 2 };
export const rougher = (a: string, b: string) => ((ROUGHNESS[a] ?? 0) >= (ROUGHNESS[b] ?? 0) ? a : b);

// Sets one cell of a list of lines in place, padding with EMPTY, and leaves the trimming to the caller.
export function layCell(lines: string[], col: number, row: number, code: string) {
	while (lines.length <= row) lines.push("");
	const line = lines[row].padEnd(col + 1, EMPTY);
	lines[row] = line.slice(0, col) + code + line.slice(col + 1);
}

// `corners` is the floor of the half a cut names, one string per row like `cuts`,
// and is left out when empty. `borders` is a letter per cell (. straight, i irregular, r rounded), left out likewise.
export type Terrain = { rows: string[]; cuts: string[]; corners?: string[]; borders?: string[] };

// The letter of the cut whose rock half is the other one: a <-> c, b <-> d.
const opposite = (letter: CutLetter) => CUT_LETTERS[(CUT_LETTERS.indexOf(letter) + 2) % CUT_LETTERS.length];

// Rows, cuts, corners and borders as the server spells them (MapLibrary.normalize_terrain): all trimmed, a cut or a
// border on a cell the rows leave uncarved dropped, a corner on a cell that is not cut dropped, and a cut cell whose
// main half is rock but whose corner is floor stored the other way round: the opposite cut, the corner's code in
// `rows`. `cuts` is always a list, empty when nothing is cut.
export function terrainOf(rows: readonly string[], cuts: CutLines, corners?: CutLines, borders?: CutLines): Terrain {
	const kept = [...rows];
	const keptCuts: string[] = [];
	const keptCorners: string[] = [];
	(cuts ?? []).forEach((line, row) => {
		let cutLine = "";
		let cornerLine = "";
		[...line].forEach((given, col) => {
			const main = kept[row]?.[col] ?? EMPTY;
			let [cut, corner] = [given, corners?.[row]?.[col] ?? EMPTY];
			if (main === EMPTY && given in CUT_CORNERS && corner !== EMPTY) {
				layCell(kept, col, row, corner);
				[cut, corner] = [opposite(given as CutLetter), EMPTY];
			} else if (main === EMPTY) {
				cut = EMPTY;
			}
			cutLine += cut;
			cornerLine += cut === EMPTY ? EMPTY : corner;
		});
		keptCuts[row] = cutLine;
		keptCorners[row] = cornerLine;
	});
	const keptBorders = trim(
		(borders ?? []).map((line, row) => [...line].map((border, col) => ((kept[row]?.[col] ?? EMPTY) === EMPTY ? EMPTY : border)).join("")),
	);
	const normal: Terrain = { rows: trim(kept), cuts: trim(keptCuts) };
	const lines = trim(keptCorners);
	return { ...normal, ...(lines.length ? { corners: lines } : {}), ...(keptBorders.length ? { borders: keptBorders } : {}) };
}

export function setBorders(borders: CutLines, cells: readonly { col: number; row: number }[], letter: string): string[] {
	const next = [...(borders ?? [])];
	for (const { col, row } of cells) layCell(next, col, row, letter);
	return next;
}

// What a scene write sends: `cuts` only when a cell is cut, `corners` only when a cut cell has floor in both halves,
// `borders` only when a cell has one.
export function terrainWrite({ rows, cuts, corners, borders }: Terrain) {
	return {
		rows,
		...(cuts.length ? { cuts } : {}),
		...(cuts.length && corners?.length ? { corners } : {}),
		...(borders?.length ? { borders } : {}),
	};
}

const spelling = ({ rows, cuts, corners, borders }: Terrain) => [rows, cuts, corners ?? [], borders ?? []].map((lines) => lines.join("\n")).join("\0");

export function sameTerrain(a: Terrain, b: Terrain) {
	return spelling(a) === spelling(b);
}

// Spelled as the server spells it: see terrainOf. A value in another shape is
// left as it is, to be refused by validation.
export function normalizeTerrain<T>(terrain: T): T | Record<string, unknown> {
	if (!terrain || typeof terrain !== "object" || Array.isArray(terrain)) return terrain;
	const { rows, cuts, corners, borders, ...rest } = terrain as Record<string, unknown>;
	if (!("rows" in terrain) || Object.keys(rest).length || !isLines(rows)) return terrain;
	const [cutLines, cornerLines, borderLines] = [cuts, corners, borders].map((lines) => (lines == null ? [] : lines));
	if (!isLines(cutLines) || !isLines(cornerLines) || !isLines(borderLines)) return terrain;
	const normal = terrainOf(rows, cutLines, cornerLines, borderLines);
	return normal.rows.length ? terrainWrite(normal) : {};
}
